import { Location } from '@angular/common';
import { Router } from '@angular/router';
import { MatBottomSheet } from '@angular/material/bottom-sheet';
import { Injectable } from '@angular/core';
import { BlogApiService } from './blog-api.service';
import { GenericResponse } from '../generic-response';
import { BlogUserModel } from 'src/app/core/model/object/blog-user-model';
import { BlogUserService } from './blog-user.service';
import { TribuneAnalyticsService } from '../analytics-repository/tribune-analytics.service';
import { RouteAnimationShared } from 'src/app/utils/route-animation-shared';
import { HttpEventType } from '@angular/common/http';
import { Subject } from 'rxjs';

@Injectable({
  providedIn: 'root',
})
export class BlogService {
  progress: Subject<number> = new Subject();
  constructor(
    private blogApiService: BlogApiService,
    private blogUserService: BlogUserService,
    private tribuneAnalytics: TribuneAnalyticsService,
    private routeAnimationShared: RouteAnimationShared,
    private router: Router,
    private location: Location
  ) {}

  loadPosts(postPerPage: number): Promise<GenericResponse> {
    if (!localStorage.getItem('blog-token')) {
      return this.blogApiService.createBlogUserToken().then((res) => {
        if (res.isSuccess()) {
          if (!localStorage.getItem('blog-user')) {
            return this.blogApiService.getBlogUser().then((result) => {
              if (result.isSuccess()) {
                return this.getBlogPosts(0, postPerPage);
              } else {
                return new GenericResponse(false);
              }
            });
          } else {
            return this.getBlogPosts(0, postPerPage);
          }
        } else {
          return new GenericResponse(false);
        }
      });
    } else {
      if (!localStorage.getItem('blog-user')) {
        return this.blogApiService.getBlogUser().then((res) => {
          if (res.isSuccess()) {
            return this.getBlogPosts(0, postPerPage);
          } else {
            return new GenericResponse(false);
          }
        });
      } else {
        return this.getBlogPosts(0, postPerPage);
      }
    }
  }

  getBlogPosts(page: number, postPerPage: number): Promise<GenericResponse> {
    return this.blogApiService.getBlogPostsList(page, postPerPage);
  }

  getBlogPostsListWithTag(
    page: number,
    postPerPage: number,
    tag: string
  ): Promise<GenericResponse> {
    return this.blogApiService.getBlogPostsListWithTag(page, postPerPage, tag);
  }
  postLike(id: string): Promise<GenericResponse> {
    return this.blogApiService.postLike(id).then((res) => {
      if (res.isSuccess()) {
        this.tribuneAnalytics.analyticTribuneLiked();
      }
      return res;
    });
  }

  postDislike(id: string): Promise<GenericResponse> {
    return this.blogApiService.postDislike(id).then((res) => {
      if (res.isSuccess()) {
        this.tribuneAnalytics.analyticTribuneLikeDeleted();
      }
      return res;
    });
  }

  searchTag(title: string): Promise<GenericResponse> {
    return this.blogApiService.searchTag(title);
  }

  searchUser(title: string): Promise<GenericResponse> {
    return this.blogApiService.searchUser(title);
  }

  searchTagAndUser(title: string): Promise<GenericResponse> {
    return this.blogApiService.searchTagAndUser(title);
  }

  getBlogPostComments(
    id: string,
    startDate: string,
    limit: number
  ): Promise<GenericResponse> {
    return this.blogApiService.getBlogPostComments(id, startDate, limit);
  }

  createReply(id: string, text: string): Promise<GenericResponse> {
    return this.blogApiService.createReply(id, text).then((res) => {
      if (res.isSuccess()) {
        this.tribuneAnalytics.analyticTribuneCommented();
      }
      return res;
    });
  }

  createComment(id: string, text: string): Promise<GenericResponse> {
    return this.blogApiService.createComment(id, text).then((res) => {
      if (res.isSuccess()) {
        this.tribuneAnalytics.analyticTribuneCommented();
      }
      return res;
    });
  }

  deleteComment(id: string): Promise<GenericResponse> {
    return this.blogApiService.deleteComment(id).then((res) => {
      if (res.isSuccess()) {
        this.tribuneAnalytics.analyticTribuneCommentDeleted();
      }
      return res;
    });
  }

  deleteReply(id: string): Promise<GenericResponse> {
    return this.blogApiService.deleteReply(id).then((res) => {
      if (res.isSuccess()) {
        this.tribuneAnalytics.analyticTribuneCommentDeleted();
      }
      return res;
    });
  }

  reportBlogPost(id: string, reason: string): Promise<GenericResponse> {
    return this.blogApiService.reportBlogPost(id, reason).then((res) => {
      if (res.isSuccess()) {
        this.tribuneAnalytics.analyticTribunePostReported();
      }
      return res;
    });
  }

  reportBlogComment(id: string, reason: string): Promise<GenericResponse> {
    return this.blogApiService.reportBlogComment(id, reason).then((res) => {
      if (res.isSuccess()) {
        this.tribuneAnalytics.analyticTribuneCommentReported();
      }
      return res;
    });
  }

  reportBlogReply(id: string, reason: string): Promise<GenericResponse> {
    return this.blogApiService.reportBlogReply(id, reason).then((res) => {
      if (res.isSuccess()) {
        this.tribuneAnalytics.analyticTribuneCommentReported();
      }
      return res;
    });
  }

  editBlogUser(
    name?: string,
    username?: string,
    bio?: string,
    status?: string,
    avatar?: string
  ): Promise<GenericResponse> {
    return this.blogApiService.editBlogUser(
      name,
      username,
      bio,
      status,
      avatar
    );
  }

  validateUsername(username: string): Promise<GenericResponse> {
    return this.blogApiService.validateUsername(username);
  }
  goToUserProfile(username: string) {
    this.routeAnimationShared.showRouteAnimation();
    this.router.navigate(['/tribune/' + username + '/profile']);
  }
  goToUserProfileSetting(username: string) {
    this.routeAnimationShared.showRouteAnimation();
    this.router.navigate(['/tribune/' + username + '/profile/edit']);
  }
  goToUserPost(username: string, id: string) {
    this.routeAnimationShared.showRouteAnimation();
    this.router.navigate(['/tribune/' + username + '/posts/' + id]);
  }
  goToFollowees(username: string) {
    this.routeAnimationShared.showRouteAnimation();
    this.router.navigate(['/tribune/' + username + '/profile/followees']);
  }
  goTonewPost(username: string) {
    this.routeAnimationShared.showRouteAnimation();
    this.router.navigate(['/tribune/' + username + '/newpost']);
  }
  goback() {
    this.routeAnimationShared.showRouteAnimation();
    this.location.back();
  }
  goToSearch() {
    this.routeAnimationShared.showRouteAnimation();
    this.router.navigate(['/tribune/search']);
  }
  goToActivity() {
    this.routeAnimationShared.showRouteAnimation();
    this.router.navigate(['/tribune/activity']);
  }
  goToSearchTag() {
    this.routeAnimationShared.showRouteAnimation();
    this.router.navigate(['/tribune/tags']);
  }
  goToSuggest() {
    this.routeAnimationShared.showRouteAnimation();
    this.router.navigate(['/tribune/suggestion']);
  }

  setBlogImage(
    avatar: FormData,
    user: BlogUserModel
  ): Promise<GenericResponse> {
    return this.blogApiService.setBlogImage(avatar).then((res) => {
      if (res.success) {
        user.avatar = '/uploads/images/avatars/' + res.imageName;
        user.avatarPath = res.path;
        this.blogUserService.saveBlogUser(user);
      }
      return new GenericResponse(res.success);
    });
  }

  createPost(body: FormData): Promise<GenericResponse> {
    return new Promise((resolve) => {
      this.blogApiService.createPost(body).subscribe((resp) => {
        if (resp.type === HttpEventType.Response) {
          resolve(new GenericResponse(true));
        }
        if (resp.type === HttpEventType.UploadProgress) {
          const percentDone = Math.round((100 * resp.loaded) / resp.total);
          this.progress.next(percentDone);
        }
      });
    });
  }

  deletePost(postId: string): Promise<GenericResponse> {
    return this.blogApiService.deletePost(postId);
  }

  getBlogUser(username: string): Promise<GenericResponse> {
    return this.blogApiService.getBlogUser2(username);
  }

  getUserPostsList(
    page: number,
    postPerPage: number,
    username: string
  ): Promise<GenericResponse> {
    return this.blogApiService.getUserPostsList(page, postPerPage, username);
  }

  getUserSuggestions(): Promise<GenericResponse> {
    return this.blogApiService.userSuggestions();
  }

  follow(userId: string): Promise<GenericResponse> {
    return this.blogApiService.follow(userId);
  }

  getActivityList(page: number, postPerPage: number): Promise<GenericResponse> {
    return this.blogApiService.getActivityList(page, postPerPage);
  }

  getActivityCount(): Promise<GenericResponse> {
    return this.blogApiService.getActivityCount();
  }

  getFollowRequests(page: number, limit: number): Promise<GenericResponse> {
    return this.blogApiService.getFollowRequests(page, limit);
  }

  setFollowRequest(userId: string, status: string): Promise<GenericResponse> {
    return this.blogApiService.setFollowRequest(userId, status);
  }

  getBlogPost(postId: string): Promise<GenericResponse> {
    return this.blogApiService.getBlogPost(postId);
  }

  userFollowers(
    page: number,
    postPerPage: number,
    userId: string
  ): Promise<GenericResponse> {
    return this.blogApiService.userFollowers(page, postPerPage, userId);
  }

  userFollowees(
    page: number,
    postPerPage: number,
    userId: string
  ): Promise<GenericResponse> {
    return this.blogApiService.userFollowees(page, postPerPage, userId);
  }

  userBlockList(): Promise<GenericResponse> {
    return this.blogApiService.userBlockList();
  }

  userBlock(userId: string): Promise<GenericResponse> {
    return this.blogApiService.userBlock(userId);
  }
}
